import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import WaterCapacityStatus from './water/WaterCapacityStatus'
import { useLocation, useNavigate } from 'react-router-dom'
import GradientButton from '../components/GradientButton'
import ContextSelectorBar from '../components/ContextSelectorBar'
import CurrentContextBar from '../components/CurrentContextBar'
import DateTimeLocalInput from '../components/DateTimeLocalInput'
import type { EntityBatchSyncStatus, EntityMutationResult, EntityMutationSyncState } from '../utils/entityMutationSync'
import { ensureFarmList } from '../utils/farmWizard'
import { lotApi } from '../utils/lotApi'
import { 
  estimateDissolvedOxygen, 
  calculateDOSaturation,
  isValidDateFormat,
  getTemperatureStatus,
  calculatePressureRatio
} from '../utils/waterCalculations'
import { normalizeToIsoZMidnight, toDateInputValue } from '../utils/dateNormalization'
import { readScopedString, writeScopedString } from '../utils/scopedStorage'
import { loadActiveContextSelection, persistActiveContextSelection } from '../utils/activeContextSelection'
import { createId } from '../utils/id'
import { useT, type TranslationKey } from '../i18n'
import { resolveFarmProductionSpeciesKey } from '../utils/farmProductionSpecies'
import { formatLotPickerLabel, isLotActiveForInteraction } from '../utils/lotInteraction'
import { loadDeletedLotLabels } from '../utils/deletedLots'
import DeletedLotRowsLine, { DELETED_LOT_ROW_STYLE } from '../components/DeletedLotRowsLine'
import { resolveUnmappedLotUiState } from '../utils/unmappedLotUi'
import { getForeignWaterLotIds, hydrateLoadedWaterRows } from './water/loadedRowLotHydration'
import { resolveWaterLotCellDisplayMode } from './water/waterLotColumnDisplay'
import {
  getDerivedManualDissolvedOxygenSaturation,
  recalculateEstimatedWaterDissolvedOxygen
} from './water/waterDissolvedOxygen'
import { createWaterEditCommitScheduler } from './water/waterEditCommitScheduler'
import {
  buildImportWorkspaceReviewNotice,
  parseImportWorkspaceReviewRouteState
} from './importWorkspace/importWorkspaceSession'
import { resolvePreferredWaterLotId } from './water/preferredLotSelection'
import { compareForSort, type SortDirection } from './datatable/datatableHelpers'
import {
  loadWaterTableRows,
  refreshWaterTableRowsFromApi,
  saveWaterTableRows,
  enqueueWaterRowsUpsert,
  upsertWaterTableRowAndSync,
  deleteWaterTableRowAndSync,
  readWaterTableRowSyncStatus,
  retryWaterTableRowSync,
  retryWaterTableRowSyncStatus,
  type WaterDataRow
} from '../utils/waterTable'
import { findFarmByIdOrName, findProductionUnitByIdOrName } from '../utils/contextSelection'
import { useStoredPrincipal } from '../hooks/useStoredPrincipal'
import { CrewCorrectionButton } from './crewCorrection/CrewCorrectionButton'
import { CrewRowBadges, useCorrectionCounts } from './crewCorrection/CrewRowBadges'
import { useActorNames } from '../utils/actorNames'
import { readFlowLpm } from '../utils/hatchery'
import { farmTimeZone } from '../utils/farmTime'

/** A water row with at least one reading (flow lives in metadata). */
export const waterRowHasReading = (row: WaterDataRow): boolean => (
  [row.temperature, row.ph, row.dissolvedOxygen, row.manualDissolvedOxygen, row.manualDissolvedOxygenSaturation, row.salinity]
    .some((value) => value !== null && value !== undefined && !Number.isNaN(value))
  || readFlowLpm(row) !== null
)

// findings #66: values of a saved water reading (not its lot label, which is
// context only — the server keeps water per unit) are audited.
const WATER_AUDITED_FIELDS = new Set<string>([
  'date',
  'temperature',
  'ph',
  'dissolvedOxygen',
  'dissolvedOxygenSaturation',
  'manualDissolvedOxygen',
  'manualDissolvedOxygenSaturation',
  'salinity',
  'notes'
])
import { deriveWaterSyncOutcome, type WaterSyncOutcome } from '../utils/waterSyncOutcome'
import {
  computeWaterSyncBannerVisibility,
  shouldPublishRefreshComplete
} from './water/waterSyncBannerVisibility'
import { buildUnitSelectOptions, isUnitIdInactive } from '../utils/unitStatus'

// Reusable styles
const styles = {
  input: {
    border: 'none',
    background: 'transparent',
    width: '100%',
    fontSize: '16px'
  },
  cell: {
    padding: '12px',
    border: '1px solid #ddd',
    minWidth: '140px',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const
  },
  dateCell: {
    padding: '12px',
    border: '1px solid #ddd',
    minWidth: '200px',
    maxWidth: '280px',
    whiteSpace: 'normal' as const
  },
  button: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 'bold'
  }
}

const WATER_EDIT_COMMIT_DELAY_MS = 600

export default function Water() {
  const location = useLocation()
  const navigate = useNavigate()
  const t = useT()

  const principal = useStoredPrincipal()
  const canEditWaterLogs = !principal || principal.role === 'editor' || principal.role === 'admin'
  const deepLink = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return {
      farmId: params.get('farmId')?.trim() || '',
      farmName: params.get('farmName')?.trim() || '',
      productionUnitId: params.get('productionUnitId')?.trim() || '',
      productionUnitName: params.get('productionUnitName')?.trim() || '',
      lotId: params.get('lotId')?.trim() || '',
      lotKey: params.get('lotKey')?.trim() || ''
    }
  }, [location.search])
  const importReviewState = useMemo(() => parseImportWorkspaceReviewRouteState(location.search), [location.search])
  const importReviewNotice = useMemo(() => {
    if (importReviewState.importSource !== 'imports') return null
    return buildImportWorkspaceReviewNotice({
      rowLabel: 'water log',
      importedRows: importReviewState.importedRows,
      skippedRows: importReviewState.skippedRows,
      queuedRows: importReviewState.queuedRows,
      localOnlyRows: importReviewState.localOnlyRows,
      blockedRows: importReviewState.blockedRows,
      syncErrorMessage: importReviewState.syncErrorMessage,
      syncState: importReviewState.syncState
    })
  }, [importReviewState])

  const [farmId, setFarmId] = useState<string>('')
  const [productionUnitId, setProductionUnitId] = useState<string>('')
  const [data, setData] = useState<WaterDataRow[]>([])
  // findings #66: rows added during this visit are drafts (typed inline, like a
  // spreadsheet). Every other row is a saved reading: its values, date and
  // notes change only through the audited "Corregir…" dialog.
  const [sessionDraftRowIds, setSessionDraftRowIds] = useState<Set<string>>(() => new Set())
  // Findings #86: a saved row with no reading yet (only a date / lot / note)
  // holds nothing to audit, so it stays editable until its first value —
  // otherwise the first reading had to go through "Corregir…".
  const isWaterRowLocked = (id: string): boolean => {
    if (sessionDraftRowIds.has(id)) return false
    const row = data.find((candidate) => candidate.id === id)
    return row ? waterRowHasReading(row) : true
  }
  // Edit handlers: editing an empty saved row makes it a draft for the rest
  // of this visit, so it does not lock after its first keystroke (the first
  // digit would otherwise count as a reading and block the second).
  const claimWaterRowForEdit = (id: string): boolean => {
    if (isWaterRowLocked(id)) return false
    if (!sessionDraftRowIds.has(id)) setSessionDraftRowIds((prev) => new Set(prev).add(id))
    return true
  }
  const actorNames = useActorNames()
  const [waterCorrectionsRefreshKey, setWaterCorrectionsRefreshKey] = useState(0)
  const [availableFarms, setAvailableFarms] = useState<any[]>([])
  const [availableProductionUnits, setAvailableProductionUnits] = useState<any[]>([])
  const selectedUnitInactive = isUnitIdInactive(availableProductionUnits, productionUnitId)
  const [invalidDates, setInvalidDates] = useState<Set<string>>(new Set())
  const [showDateNotification, setShowDateNotification] = useState(false)
  const [availableLots, setAvailableLots] = useState<{ id: string; lotKey: string; stage?: string }[]>([])
  // Farm-wide lot roster (all production units) — used only to recognise a
  // lot that was transferred out of this unit so its readings stay labelled
  // instead of showing "(ninguno)". Track A finding 4.2 (Water parity).
  const [farmLots, setFarmLots] = useState<{ id: string; lotKey: string; stage?: string }[]>([])
  // Findings #133: readings of a deleted lot name it instead of showing "(ninguno)".
  const [deletedLotLabels, setDeletedLotLabels] = useState<Map<string, string>>(new Map())
  const [isContextSwitching, setIsContextSwitching] = useState(false)
  const [lotsLoading, setLotsLoading] = useState(false)
  const [lotAutoClearedIds, setLotAutoClearedIds] = useState<Set<string>>(new Set())
  const [preferredLotId, setPreferredLotId] = useState<string>('')
  const [sortConfig, setSortConfig] = useState<{ key: keyof WaterDataRow; direction: SortDirection } | null>(null)
  const tableScrollRef = useRef<HTMLDivElement | null>(null)
  const tableScrollTopRef = useRef<HTMLDivElement | null>(null)
  const tableRef = useRef<HTMLTableElement | null>(null)
  const [tableWidth, setTableWidth] = useState<number>(0)
  const [waterTableStatusMessage, setWaterTableStatusMessage] = useState<string | null>(null)
  const [waterSyncStatus, setWaterSyncStatus] = useState<EntityBatchSyncStatus | null>(null)
  const [waterSyncLastOutcome, setWaterSyncLastOutcome] = useState<(WaterSyncOutcome & { attemptedAtIso: string }) | null>(null)
  const [retryingWaterSync, setRetryingWaterSync] = useState(false)
  const [waterTableRefreshing, setWaterTableRefreshing] = useState(false)
  const waterTableStatusVersionRef = useRef(0)
  const pendingWaterActionRef = useRef<'saved' | 'deleted' | null>(null)
  const pendingWaterRowIdsRef = useRef<Set<string>>(new Set())
  // v2.1 finding #61 (WSL-09, 2026-07-01): forward-ref indirection so
  // `applyWaterTableStatus` (declared above `refreshCurrentWaterSyncStatus`)
  // can call the outbox-snapshot refresher from its `syncPromise.then`
  // callback without a circular dependency. Mirrors the Biomass fix. The
  // `useEffect` below `refreshCurrentWaterSyncStatus` keeps the ref pointed
  // at the latest identity.
  const refreshWaterSyncStatusFnRef = useRef<() => Promise<EntityBatchSyncStatus | null>>(async () => null)
  const waterEditCommitSchedulerRef = useRef(createWaterEditCommitScheduler(WATER_EDIT_COMMIT_DELAY_MS))
  const isMountedRef = useRef(true)

  const scrollDateInputIntoView = (input: HTMLInputElement) => {
    const container = tableScrollRef.current
    if (!container) return

    const inputRect = input.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()
    const padding = 12

    const leftOverflow = inputRect.left - containerRect.left
    const rightOverflow = inputRect.right - containerRect.right

    if (leftOverflow < padding) {
      container.scrollLeft += leftOverflow - padding
    } else if (rightOverflow > -padding) {
      container.scrollLeft += rightOverflow + padding
    }
  }

  // Memoized farm altitude lookup to prevent repeated array searching
  const currentFarmAltitude = useMemo(() => {
    const selectedFarm = availableFarms.find((farm: any) => String(farm?.id ?? '') === farmId)
    return selectedFarm?.altitude || 0
  }, [farmId, availableFarms])

  const selectedFarm = useMemo(() => (
    availableFarms.find((farm: any) => String(farm?.id ?? '') === farmId)
  ), [availableFarms, farmId])
  useEffect(() => {
    const farmKey = String(selectedFarm?.id ?? '')
    if (!farmKey) return
    let cancelled = false
    void loadDeletedLotLabels(farmKey).then((labels) => {
      if (!cancelled) setDeletedLotLabels(labels)
    })
    return () => {
      cancelled = true
    }
  }, [selectedFarm?.id])

  const farmSpeciesKey = useMemo(() => resolveFarmProductionSpeciesKey(selectedFarm), [selectedFarm])

  // Findings #85: readings are dated and shown in the farm's time zone.
  const waterTimeZone = useMemo(() => farmTimeZone(selectedFarm), [selectedFarm])

  const selectedProductionUnit = useMemo(() => {
    if (!selectedFarm) return undefined
    const units = Array.isArray(selectedFarm.productionUnits) ? selectedFarm.productionUnits : []
    return units.find((unit: any) => String(unit?.id ?? '') === productionUnitId)
  }, [selectedFarm, productionUnitId])

  // Memoized pressure ratio calculation for performance
  const currentPressureRatio = useMemo(() => {
    return calculatePressureRatio(currentFarmAltitude)
  }, [currentFarmAltitude])

  // Get current farm altitude (kept for backward compatibility)
  const getCurrentFarmAltitude = (): number => currentFarmAltitude

  const getWaterContext = () => {
    if (!selectedFarm || !selectedProductionUnit) return null
    return {
      farmId: String(selectedFarm.id ?? ''),
      productionUnitId: String(selectedProductionUnit.id ?? ''),
      farmName: String(selectedFarm.name ?? farmId),
      productionUnitName: String(selectedProductionUnit.name ?? productionUnitId)
    }
  }

  // Crew Mode supervisor correction v0 — a correction is a direct server write
  // (outside the outbox), so pull the authoritative rows back afterward.
  const reloadWaterFromApi = useCallback(async () => {
    const context = getWaterContext()
    if (!context) return
    try {
      const refreshed = await refreshWaterTableRowsFromApi(context)
      const refreshedWithEstimated = recalculateEstimatedWaterDissolvedOxygen(
        hydrateLoadedWaterRows(refreshed, availableLots, farmLots),
        getCurrentFarmAltitude()
      )
      setData(refreshedWithEstimated)
      saveWaterTableRows(context, refreshedWithEstimated, { skipSync: true })
    } catch {
      // local-first; ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableLots, farmLots, selectedFarm, selectedProductionUnit])

  const getWaterTableStatusKey = useCallback((
    verb: 'saved' | 'deleted',
    state: EntityMutationSyncState
  ): TranslationKey => {
    if (state === 'synced') return `water.status.${verb}Synced` as TranslationKey
    if (state === 'offline') return `water.status.${verb}Offline` as TranslationKey
    return `water.status.${verb}Pending` as TranslationKey
  }, [])

  const trackWaterRowIds = useCallback((rowIds: string[], mode: 'add' | 'remove'): number => {
    const next = new Set(pendingWaterRowIdsRef.current)
    rowIds.forEach((rowId) => {
      const stableId = String(rowId ?? '').trim()
      if (!stableId) return
      if (mode === 'add') next.add(stableId)
      else next.delete(stableId)
    })
    pendingWaterRowIdsRef.current = next
    return next.size
  }, [])

  const applyWaterTableStatus = useCallback((
    verb: 'saved' | 'deleted',
    rowIds: string[],
    result: Pick<EntityMutationResult<unknown>, 'syncState' | 'syncPromise'>
  ) => {
    const stableRowIds = rowIds.map((rowId) => String(rowId ?? '').trim()).filter(Boolean)
    const nextVersion = waterTableStatusVersionRef.current + 1
    waterTableStatusVersionRef.current = nextVersion

    if (result.syncState === 'synced') {
      const remaining = trackWaterRowIds(stableRowIds, 'remove')
      if (remaining === 0) {
        pendingWaterActionRef.current = null
      }
    } else {
      trackWaterRowIds(stableRowIds, 'add')
      pendingWaterActionRef.current = verb
    }

    setWaterTableStatusMessage(t(getWaterTableStatusKey(verb, result.syncState)))

    if (!result.syncPromise) return

    void result.syncPromise.then((nextState) => {
      if (waterTableStatusVersionRef.current !== nextVersion) return

      if (nextState === 'synced') {
        const remaining = trackWaterRowIds(stableRowIds, 'remove')
        if (remaining === 0) {
          pendingWaterActionRef.current = null
        }
      } else {
        trackWaterRowIds(stableRowIds, 'add')
        pendingWaterActionRef.current = verb
      }

      setWaterTableStatusMessage(t(getWaterTableStatusKey(verb, nextState)))

      // v2.1 finding #61 (WSL-09): reconcile the attention-banner snapshot
      // so it flips from 'pending' to 'synced' immediately after the drain.
      // Without this, finding #58's `showActionMessage` rule keeps the
      // stale attention banner pinned while suppressing the post-save toast
      // — operator-visible regression is "save appears stuck".
      void refreshWaterSyncStatusFnRef.current()
    })
  }, [getWaterTableStatusKey, t, trackWaterRowIds])

  const clearWaterTableStatus = useCallback(() => {
    waterTableStatusVersionRef.current += 1
    pendingWaterActionRef.current = null
    pendingWaterRowIdsRef.current = new Set()
    setWaterTableStatusMessage(null)
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadFarms = async () => {
      const farms = await ensureFarmList()
      if (cancelled) return

      setAvailableFarms(farms)

      // Load previously selected farm and production unit (deep-link overrides saved selection)
      const savedFarmToken = readScopedString('water-selected-farm')
      const savedProductionUnitToken = readScopedString('water-selected-production-unit')

      const activeContext = loadActiveContextSelection()

      const farmToken = deepLink.farmId || deepLink.farmName || activeContext.farmId || savedFarmToken || ''
      const resolvedFarm = findFarmByIdOrName(farms, farmToken)
      if (resolvedFarm?.id) {
        setFarmId(String(resolvedFarm.id))
      } else if (farms.length > 0) {
        setFarmId(String(farms[0].id))
      }

      const productionUnitToken = deepLink.productionUnitId || deepLink.productionUnitName || activeContext.productionUnitId || savedProductionUnitToken || ''
      const resolvedUnit = findProductionUnitByIdOrName(resolvedFarm ?? farms[0], productionUnitToken)
      if (resolvedUnit?.id) {
        setProductionUnitId(String(resolvedUnit.id))
      }
    }

    void loadFarms()

    return () => {
      cancelled = true
    }
  }, [deepLink.farmId, deepLink.farmName, deepLink.lotId, deepLink.productionUnitId, deepLink.productionUnitName])

  useEffect(() => {
    if (!farmId) {
      setAvailableProductionUnits([])
      setProductionUnitId('')
      return
    }

    const selectedFarm = availableFarms.find((farm: any) => String(farm?.id ?? '') === farmId)
    if (selectedFarm) {
      const units = selectedFarm.productionUnits || []
      setAvailableProductionUnits(units)

      if (units.length === 0) {
        setProductionUnitId('')
      } else {
        const unitToken = deepLink.productionUnitId || deepLink.productionUnitName || productionUnitId
        const resolvedUnit = findProductionUnitByIdOrName(selectedFarm, unitToken)
        if (resolvedUnit?.id) {
          setProductionUnitId(String(resolvedUnit.id))
        } else if (!productionUnitId || !units.some((unit: any) => String(unit?.id ?? '') === productionUnitId)) {
          setProductionUnitId(String(units[0].id))
        }
      }
    } else {
      setAvailableProductionUnits([])
      setProductionUnitId('')
    }
    
    // Save selection to localStorage (store IDs; legacy name values are migrated on read)
    writeScopedString('water-selected-farm', farmId)
    persistActiveContextSelection({ farmId })
  }, [deepLink.productionUnitId, deepLink.productionUnitName, farmId, availableFarms, productionUnitId])

  useEffect(() => {
    const resolvedPreferredLotId = resolvePreferredWaterLotId({
      lotId: deepLink.lotId,
      lotKey: deepLink.lotKey,
      availableLots
    })

    if (resolvedPreferredLotId !== preferredLotId) {
      setPreferredLotId(resolvedPreferredLotId)
    }
  }, [availableLots, deepLink.lotId, deepLink.lotKey, preferredLotId])

  // Recalculate DO values when farm changes (altitude might be different)
  useEffect(() => {
    if (data.length > 0 && farmId) {
      const updatedData = recalculateEstimatedWaterDissolvedOxygen(data, getCurrentFarmAltitude())
      if (JSON.stringify(updatedData) !== JSON.stringify(data)) {
        saveData(updatedData)
      }
    }
  }, [farmId])

  // Save production unit selection
  useEffect(() => {
    if (productionUnitId) {
      writeScopedString('water-selected-production-unit', productionUnitId)
      persistActiveContextSelection({ productionUnitId })
    }
  }, [productionUnitId])

  useEffect(() => {
    const tableEl = tableRef.current
    if (tableEl) {
      setTableWidth(tableEl.scrollWidth)
    }
  }, [data])

  const syncScroll = (source: 'top' | 'main') => (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollLeft } = event.currentTarget
    if (source !== 'main' && tableScrollRef.current && tableScrollRef.current.scrollLeft !== scrollLeft) {
      tableScrollRef.current.scrollLeft = scrollLeft
    }
    if (source !== 'top' && tableScrollTopRef.current && tableScrollTopRef.current.scrollLeft !== scrollLeft) {
      tableScrollTopRef.current.scrollLeft = scrollLeft
    }
  }

  // Load water data when farm/production unit changes
  useEffect(() => {
    if (!farmId || !productionUnitId) {
      setIsContextSwitching(false)
      return
    }
    if (!selectedFarm || !selectedProductionUnit) {
      setData([])
      setIsContextSwitching(false)
      return
    }

    const context = {
      farmId: String(selectedFarm.id ?? ''),
      productionUnitId: String(selectedProductionUnit.id ?? ''),
      farmName: String(selectedFarm.name ?? farmId),
      productionUnitName: String(selectedProductionUnit.name ?? productionUnitId)
    }

    const loaded = hydrateLoadedWaterRows(loadWaterTableRows(context), availableLots, farmLots)
    const withEstimated = recalculateEstimatedWaterDissolvedOxygen(loaded, getCurrentFarmAltitude())
    setData(withEstimated)
    setIsContextSwitching(false)
    saveWaterTableRows(context, withEstimated, { skipSync: true })

    // Non-blocking online refresh. cancelled flag prevents a stale response
    // from a previous PU overwriting current data after the user has switched.
    let cancelled = false
    void refreshWaterTableRowsFromApi(context)
      .then((refreshed) => {
        if (cancelled) return
        const refreshedWithEstimated = recalculateEstimatedWaterDissolvedOxygen(
          hydrateLoadedWaterRows(refreshed, availableLots, farmLots),
          getCurrentFarmAltitude()
        )
        setData(refreshedWithEstimated)
        saveWaterTableRows(context, refreshedWithEstimated, { skipSync: true })
      })
      .catch(() => {
        // ignore; local-first
      })

    return () => {
      cancelled = true
    }
  }, [farmId, productionUnitId, selectedFarm, selectedProductionUnit])

  useEffect(() => {
    if (availableLots.length === 0 || data.length === 0) return
    if (!selectedFarm || !selectedProductionUnit) return

    const hydratedRows = hydrateLoadedWaterRows(data, availableLots, farmLots)
    if (hydratedRows === data) return

    const context = {
      farmId: String(selectedFarm.id ?? ''),
      productionUnitId: String(selectedProductionUnit.id ?? ''),
      farmName: String(selectedFarm.name ?? farmId),
      productionUnitName: String(selectedProductionUnit.name ?? productionUnitId)
    }

    const hydratedWithEstimates = recalculateEstimatedWaterDissolvedOxygen(hydratedRows, getCurrentFarmAltitude())
    setData(hydratedWithEstimates)
    saveWaterTableRows(context, hydratedWithEstimates, { skipSync: true })
  }, [availableLots, farmLots, data, farmId, productionUnitId, selectedFarm, selectedProductionUnit])

  // Load roster lots for this farm + production unit (used for Lot dropdown + validation)
  useEffect(() => {
    let cancelled = false

    const cacheKey = selectedFarm && selectedProductionUnit
      ? `lots-roster:${String(selectedFarm.id ?? '')}:${String(selectedProductionUnit.id ?? '')}`
      : ''
    const farmCacheKey = selectedFarm
      ? `lots-roster-farm:${String(selectedFarm.id ?? '')}`
      : ''

    const toRosterEntry = (lot: { id: unknown; lotKey?: unknown; stage?: string }) => ({
      id: String(lot.id),
      lotKey: String(lot.lotKey ?? ''),
      stage: lot.stage
    })

    // Prefer cached lots immediately (supports offline / flaky networks).
    if (cacheKey) {
      const cached = readScopedString(cacheKey)
      if (cached) {
        try {
          const parsed = JSON.parse(cached) as Array<{ id: string; lotKey: string; stage?: string }>
          if (Array.isArray(parsed)) {
            setAvailableLots(parsed.map(toRosterEntry))
          }
        } catch {
          // ignore cache parse errors
        }
      }
    }
    if (farmCacheKey) {
      const cached = readScopedString(farmCacheKey)
      if (cached) {
        try {
          const parsed = JSON.parse(cached) as Array<{ id: string; lotKey: string; stage?: string }>
          if (Array.isArray(parsed)) {
            setFarmLots(parsed.map(toRosterEntry))
          }
        } catch {
          // ignore cache parse errors
        }
      }
    }

    const loadLotsForContext = async () => {
      if (!selectedFarm || !selectedProductionUnit) {
        setAvailableLots([])
        setFarmLots([])
        setLotAutoClearedIds(new Set())
        return
      }

      setLotsLoading(true)
      try {
        const [lots, allFarmLots] = await Promise.all([
          lotApi.list({
            farmId: String(selectedFarm.id ?? ''),
            productionUnitId: String(selectedProductionUnit.id ?? '')
          }),
          lotApi.list({ farmId: String(selectedFarm.id ?? '') })
        ])
        if (cancelled) return
        const nextLots = lots.map(toRosterEntry)
        const nextFarmLots = allFarmLots.map(toRosterEntry)
        setAvailableLots(nextLots)
        setFarmLots(nextFarmLots)
        try {
          if (cacheKey) writeScopedString(cacheKey, JSON.stringify(nextLots))
          if (farmCacheKey) writeScopedString(farmCacheKey, JSON.stringify(nextFarmLots))
        } catch {
          // ignore
        }
      } catch (_error) {
        if (cancelled) return
        // Keep any cached lots; do not wipe the roster on transient/offline failures.
      } finally {
        if (!cancelled) setLotsLoading(false)
      }
    }

    void loadLotsForContext()

    return () => {
      cancelled = true
    }
  }, [selectedFarm, selectedProductionUnit])

  // Manual lot mapping mode:
  // Do not auto-clear/auto-resolve lot fields based on the roster.
  // Mapping should be explicit via the UI.

  // Check for invalid date formats in loaded data
  useEffect(() => {
    const invalidIds = new Set<string>()
    data.forEach(row => {
      if (!isValidDateFormat(row.date)) {
        invalidIds.add(row.id)
      }
    })
    setInvalidDates(invalidIds)
  }, [data])

  // Save data to localStorage
  const saveData = (newData: WaterDataRow[], options?: { syncRows?: WaterDataRow[] }) => {
    const context = getWaterContext()
    const dataWithEstimates = recalculateEstimatedWaterDissolvedOxygen(newData, getCurrentFarmAltitude())
    setData(dataWithEstimates)

    if (!farmId || !productionUnitId || !context) {
      return { context: null, dataWithEstimates }
    }

    saveWaterTableRows(context, dataWithEstimates, { skipSync: true })

    if (canEditWaterLogs && options?.syncRows && options.syncRows.length > 0) {
      const byId = new Map(dataWithEstimates.map((row) => [row.id, row]))
      const rowsToSync = options.syncRows
        .map((row) => byId.get(row.id))
        .filter((row): row is WaterDataRow => Boolean(row))
      rowsToSync.forEach((row) => {
        waterEditCommitSchedulerRef.current.cancel(row.id)
      })
      void enqueueWaterRowsUpsert(context, rowsToSync)
    }

    return { context, dataWithEstimates }
  }

  const commitWaterRowUpsert = useCallback(async (
    context: NonNullable<ReturnType<typeof getWaterContext>>,
    dataWithEstimates: WaterDataRow[],
    updatedRow: WaterDataRow
  ): Promise<void> => {
    try {
      const result = await upsertWaterTableRowAndSync(context, dataWithEstimates, updatedRow)
      if (isMountedRef.current) {
        applyWaterTableStatus('saved', [updatedRow.id], result)
      }
    } catch (error) {
      console.warn('[Water] failed to queue row upsert', error)
    }
  }, [applyWaterTableStatus])

  const flushPendingWaterRowUpsert = useCallback(async (rowId: string): Promise<void> => {
    await waterEditCommitSchedulerRef.current.flush(rowId)
  }, [])

  const flushPendingWaterEdits = useCallback(async (): Promise<void> => {
    try {
      await waterEditCommitSchedulerRef.current.flushAll()
    } catch (error) {
      console.warn('[Water] failed to flush pending row upserts', error)
    }
  }, [])

  const refreshCurrentWaterSyncStatus = useCallback(async (): Promise<EntityBatchSyncStatus | null> => {
    const rowIds = Array.from(new Set(data.map((row) => row.id).filter(Boolean)))
    if (rowIds.length === 0) {
      const emptyStatus: EntityBatchSyncStatus = {
        syncState: 'synced',
        blockedCount: 0,
        errorMessage: null,
        blockers: []
      }
      setWaterSyncStatus(emptyStatus)
      return emptyStatus
    }

    try {
      const status = await readWaterTableRowSyncStatus(rowIds)
      setWaterSyncStatus(status)
      return status
    } catch {
      setWaterSyncStatus(null)
      return null
    }
  }, [data])

  // v2.1 finding #61 (WSL-09): keep the post-drain refresh ref pointed at
  // the latest `refreshCurrentWaterSyncStatus` identity so
  // `applyWaterTableStatus` (declared earlier in the file) can call through
  // it from its `syncPromise.then` callback. See the ref's declaration
  // block above for the full rationale.
  useEffect(() => {
    refreshWaterSyncStatusFnRef.current = refreshCurrentWaterSyncStatus
  }, [refreshCurrentWaterSyncStatus])

  const retryPendingWaterTableSync = useCallback(async (): Promise<void> => {
    await flushPendingWaterEdits()

    const action = pendingWaterActionRef.current
    const rowIds = Array.from(pendingWaterRowIdsRef.current)
    const context = getWaterContext()

    if (!context || !action || rowIds.length === 0) return

    const nextState = await retryWaterTableRowSync(rowIds)
    if (nextState === 'synced') {
      pendingWaterRowIdsRef.current = new Set()
      pendingWaterActionRef.current = null
    }

    setWaterTableStatusMessage(t(getWaterTableStatusKey(action, nextState)))

    try {
      const refreshed = await refreshWaterTableRowsFromApi(context)
      const refreshedWithEstimated = recalculateEstimatedWaterDissolvedOxygen(
        hydrateLoadedWaterRows(refreshed, availableLots, farmLots),
        getCurrentFarmAltitude()
      )
      setData(refreshedWithEstimated)
      saveWaterTableRows(context, refreshedWithEstimated, { skipSync: true })
    } catch {
      // keep local state when remote refresh is unavailable
    }
    await refreshCurrentWaterSyncStatus()
  }, [availableLots, farmLots, flushPendingWaterEdits, getWaterTableStatusKey, refreshCurrentWaterSyncStatus, t, selectedFarm, selectedProductionUnit, farmId, productionUnitId])

  const refreshWaterLogs = useCallback(async (): Promise<void> => {
    const context = getWaterContext()
    if (!context) return

    await flushPendingWaterEdits()

    const nextVersion = waterTableStatusVersionRef.current + 1
    waterTableStatusVersionRef.current = nextVersion
    setWaterTableRefreshing(true)
    setWaterTableStatusMessage(t('water.status.refreshing'))

    const action = pendingWaterActionRef.current
    const rowIds = Array.from(pendingWaterRowIdsRef.current)
    const nextState = action && rowIds.length > 0
      ? await retryWaterTableRowSync(rowIds)
      : null

    if (nextState === 'synced') {
      pendingWaterRowIdsRef.current = new Set()
      pendingWaterActionRef.current = null
    }

    try {
      const refreshed = await refreshWaterTableRowsFromApi(context)
      const refreshedWithEstimated = recalculateEstimatedWaterDissolvedOxygen(
        hydrateLoadedWaterRows(refreshed, availableLots, farmLots),
        getCurrentFarmAltitude()
      )
      setData(refreshedWithEstimated)
      saveWaterTableRows(context, refreshedWithEstimated, { skipSync: true })
      const nextSyncStatus = await refreshCurrentWaterSyncStatus()

      if (waterTableStatusVersionRef.current !== nextVersion) {
        setWaterTableRefreshing(false)
        return
      }

      if (nextSyncStatus) {
        const outcome = deriveWaterSyncOutcome({
          status: nextSyncStatus,
          skippedNotFoundCount: 0
        })
        setWaterSyncLastOutcome({
          ...outcome,
          attemptedAtIso: new Date().toISOString()
        })
      }

      if (action && nextState) {
        setWaterTableStatusMessage(t(getWaterTableStatusKey(action, nextState)))
      } else if (shouldPublishRefreshComplete(nextSyncStatus?.syncState ?? null)) {
        // v2.1 finding #53: only publish the pull-success toast when the
        // push side is also fully synced — otherwise the toast contradicts
        // the still-pending attention banner. When push is still pending
        // / offline / blocked, clear any in-flight "refreshing..." toast
        // and let the attention banner be the authoritative signal.
        setWaterTableStatusMessage(t('water.status.refreshComplete'))
      } else {
        setWaterTableStatusMessage(null)
      }
    } catch {
      if (waterTableStatusVersionRef.current !== nextVersion) {
        setWaterTableRefreshing(false)
        return
      }

      if (action && nextState && nextState !== 'synced') {
        setWaterTableStatusMessage(t(getWaterTableStatusKey(action, nextState)))
      } else {
        setWaterTableStatusMessage(t('water.status.refreshFailed'))
      }
      await refreshCurrentWaterSyncStatus()
    }

    setWaterTableRefreshing(false)
  }, [availableLots, farmLots, flushPendingWaterEdits, getWaterTableStatusKey, refreshCurrentWaterSyncStatus, t, selectedFarm, selectedProductionUnit, farmId, productionUnitId])

  const waterSyncAttentionMessage = useMemo(() => {
    if (!waterSyncStatus || waterSyncStatus.syncState === 'synced') return null

    if (waterSyncStatus.blockedCount > 0) {
      return waterSyncStatus.errorMessage
        ? t('water.sync.blockedWithReason', { count: waterSyncStatus.blockedCount, reason: waterSyncStatus.errorMessage })
        : t('water.sync.blocked', { count: waterSyncStatus.blockedCount })
    }

    if (waterSyncStatus.syncState === 'offline') {
      return t('water.sync.retryOffline')
    }

    return t('water.sync.retryPending')
  }, [t, waterSyncStatus])

  const waterSyncLastResultSummary = useMemo(() => {
    if (!waterSyncLastOutcome) return null

    const resultMessage = t(waterSyncLastOutcome.resultKey, waterSyncLastOutcome.resultParams)
    const attemptedAtLabel = new Date(waterSyncLastOutcome.attemptedAtIso).toLocaleString()

    return {
      resultLabel: t('water.sync.lastResult', { result: resultMessage }),
      attemptedAtLabel: t('water.sync.lastAttempted', { time: attemptedAtLabel })
    }
  }, [t, waterSyncLastOutcome])

  const handleWaterSyncRetry = useCallback(async (): Promise<void> => {
    await flushPendingWaterEdits()

    const rowIds = Array.from(new Set(data.map((row) => row.id).filter(Boolean)))
    if (rowIds.length === 0) {
      setWaterSyncStatus({
        syncState: 'synced',
        blockedCount: 0,
        errorMessage: null,
        blockers: []
      })
      return
    }

    const context = getWaterContext()
    setRetryingWaterSync(true)

    try {
      const status = await retryWaterTableRowSyncStatus(rowIds)
      setWaterSyncStatus(status)

      const outcome = deriveWaterSyncOutcome({
        status,
        skippedNotFoundCount: 0
      })
      setWaterSyncLastOutcome({
        ...outcome,
        attemptedAtIso: new Date().toISOString()
      })
      setWaterTableStatusMessage(t(outcome.resultKey, outcome.resultParams))

      if (context) {
        try {
          const refreshed = await refreshWaterTableRowsFromApi(context)
          const refreshedWithEstimated = recalculateEstimatedWaterDissolvedOxygen(
            hydrateLoadedWaterRows(refreshed, availableLots, farmLots),
            getCurrentFarmAltitude()
          )
          setData(refreshedWithEstimated)
          saveWaterTableRows(context, refreshedWithEstimated, { skipSync: true })
        } catch {
          // Keep local state when remote refresh is unavailable.
        }
      }

      await refreshCurrentWaterSyncStatus()
    } finally {
      setRetryingWaterSync(false)
    }
  }, [availableLots, farmLots, data, flushPendingWaterEdits, refreshCurrentWaterSyncStatus, t, selectedFarm, selectedProductionUnit, farmId, productionUnitId])

  useEffect(() => {
    clearWaterTableStatus()
    setWaterTableRefreshing(false)
  }, [clearWaterTableStatus, farmId, productionUnitId])

  useEffect(() => {
    if (!farmId || !productionUnitId) {
      setWaterSyncStatus(null)
      return
    }

    void refreshCurrentWaterSyncStatus()
  }, [farmId, productionUnitId, data, refreshCurrentWaterSyncStatus])

  useEffect(() => {
    const onOnline = () => {
      void retryPendingWaterTableSync()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      void retryPendingWaterTableSync()
    }

    const onFocus = () => {
      void retryPendingWaterTableSync()
    }

    globalThis.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisibilityChange)
    globalThis.addEventListener('focus', onFocus)

    return () => {
      globalThis.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      globalThis.removeEventListener('focus', onFocus)
    }
  }, [retryPendingWaterTableSync])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      void waterEditCommitSchedulerRef.current.flushAll().catch((error) => {
        console.warn('[Water] failed to flush pending row upserts during cleanup', error)
      })
    }
  }, [])

  const persistWaterRowUpsert = (newData: WaterDataRow[], rowId: string, options?: { immediate?: boolean }) => {
    const { context, dataWithEstimates } = saveData(newData)
    if (!context) return

    const updatedRow = dataWithEstimates.find((row) => row.id === rowId)
    if (!updatedRow) return

    if (options?.immediate) {
      waterEditCommitSchedulerRef.current.cancel(updatedRow.id)
      void commitWaterRowUpsert(context, dataWithEstimates, updatedRow)
      return
    }

    waterEditCommitSchedulerRef.current.schedule(updatedRow.id, () => commitWaterRowUpsert(context, dataWithEstimates, updatedRow))
  }

  // findings #66: every delete carries a reason; the server audits it (full
  // prior row kept) when the outbox syncs. Resolves after that sync attempt so
  // a reload no longer sees the row.
  const persistWaterRowDelete = async (newData: WaterDataRow[], rowId: string, reason: string) => {
    waterEditCommitSchedulerRef.current.cancel(rowId)

    const { context, dataWithEstimates } = saveData(newData)

    if (!context) return

    try {
      const result = await deleteWaterTableRowAndSync(context, dataWithEstimates, rowId, { reason })
      applyWaterTableStatus('deleted', [rowId], result)
      await result.syncPromise?.catch(() => undefined)
    } catch (error) {
      console.warn('[Water] failed to queue row delete', error)
    }
  }

  // Export data as CSV
  const exportCSV = () => {
    if (!data.length) return;
    const headers = Object.keys(data[0]);
    const csvRows = [headers.join(",")];
    data.forEach(row => {
      const vals = headers.map(h => {
        let v = row[h as keyof WaterDataRow];
        if (h === 'date' && typeof v === 'string') {
          v = toDateInputValue(v)
        }
        if (v === null || v === undefined) return '';
        let s = String(v);
        if (/[",\n]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
        return s;
      });
      csvRows.push(vals.join(","));
    });
    const blob = new Blob([csvRows.join("\n")], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `water-data-${farmId}-${productionUnitId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const addRow = () => {
    if (!canEditWaterLogs) {
      alert(t('water.readonly.cannotAddMeasurements'))
      return
    }
    const lotKeyById = new Map(availableLots.map((lot) => [String(lot.id), String(lot.lotKey ?? '')] as const))
    const preferredLotKey = preferredLotId ? (lotKeyById.get(String(preferredLotId)) ?? '') : ''

    const newRow: WaterDataRow = {
      id: createId('water'),
      // Findings #85 / #87: the reading's moment (date + time, shown in farm
      // time). The old default was midnight UTC of the *UTC* day, which after
      // 19:00 in Peru was already tomorrow and carried no time of day.
      date: new Date().toISOString(),
      lot: preferredLotKey,
      lotId: preferredLotId,
      unmappedLot: '',
      temperature: null,
      ph: null,
      dissolvedOxygen: null,
      dissolvedOxygenSaturation: null,
      manualDissolvedOxygen: null,
      manualDissolvedOxygenSaturation: null,
      salinity: null,
      notes: '',
      metadata: {
        sourceType: 'manual'
      }
    }
    const newData = [newRow, ...data]
    setSessionDraftRowIds((prev) => new Set(prev).add(newRow.id))
    persistWaterRowUpsert(newData, newRow.id, { immediate: true })
  }

  const deleteRow = async (id: string, reason: string) => {
    if (!canEditWaterLogs) {
      alert(t('water.readonly.cannotDeleteMeasurements'))
      return
    }
    const newData = data.filter(row => row.id !== id)
    setSessionDraftRowIds((prev) => {
      if (!prev.has(id)) return prev
      const next = new Set(prev)
      next.delete(id)
      return next
    })
    await persistWaterRowDelete(newData, id, reason)
  }

  const clearUnmappedLotForRow = (rowId: string) => {
    if (!canEditWaterLogs) return
    const nextData = data.map((r) => {
      if (r.id !== rowId) return r
      return { ...r, lotId: '', lot: '', unmappedLot: '' }
    })

    if (lotAutoClearedIds.has(rowId)) {
      setLotAutoClearedIds((prev) => {
        const cp = new Set(prev)
        cp.delete(rowId)
        return cp
      })
    }

    const updated = nextData.find((r) => r.id === rowId)
    saveData(nextData, updated ? { syncRows: [updated] } : undefined)
  }

  const updateCell = (id: string, field: keyof WaterDataRow, value: string | number | null) => {
    if (!canEditWaterLogs) {
      return
    }
    if (WATER_AUDITED_FIELDS.has(field) && !claimWaterRowForEdit(id)) return
    // Date validation: always store as ISO 8601 UTC string (same as Datatable.tsx)
    if (field === 'date' && typeof value === 'string') {
      const normalized = normalizeToIsoZMidnight(value)
      value = normalized.ok ? normalized.isoZ : value
      const isValid = isValidDateFormat(value)
      setInvalidDates(prev => {
        const newSet = new Set(prev)
        if (!isValid && typeof value === 'string' && value.trim() !== '') {
          newSet.add(id)
        } else {
          newSet.delete(id)
        }
        return newSet
      })
      
      // Show notification if there are invalid dates (same as Datatable.tsx)
      if (!isValid && typeof value === 'string' && value.trim() !== '') {
        setShowDateNotification(true)
        setTimeout(() => setShowDateNotification(false), 5000)
      }
    }

    const newData = data.map(row => {
      if (row.id === id) {
        const updatedRow = { ...row, [field]: value }
        
        // If manual DO is updated and we have temperature, calculate manual saturation
        if (field === 'manualDissolvedOxygen' && value !== null && updatedRow.temperature !== null) {
          const farmAltitude = getCurrentFarmAltitude()
          updatedRow.manualDissolvedOxygenSaturation = calculateDOSaturation(value as number, updatedRow.temperature, farmAltitude)
        }
        
        return updatedRow
      }
      return row
    })

    const updated = newData.find((row) => row.id === id)

    if (field === 'lot' && lotAutoClearedIds.has(id)) {
      setLotAutoClearedIds((prev) => {
        const cp = new Set(prev)
        cp.delete(id)
        return cp
      })
    }

    if (!updated) return
    persistWaterRowUpsert(newData, updated.id)
  }

  // Hatchery v0 (findings #84): flow (L/min) is not a WaterLog column; it
  // travels in `metadata.flowLpm`. Crew rows stay locked (Corregir).
  const handleFlowInput = (id: string, value: string) => {
    if (!canEditWaterLogs || !claimWaterRowForEdit(id)) return
    const numValue = value === '' ? null : parseFloat(value)
    if (value !== '' && (numValue === null || isNaN(numValue) || numValue < 0)) return
    const newData = data.map((row) => {
      if (row.id !== id) return row
      const metadata = { ...(row.metadata ?? {}) }
      if (numValue === null) delete metadata.flowLpm
      else metadata.flowLpm = numValue
      return { ...row, metadata }
    })
    persistWaterRowUpsert(newData, id)
  }

  const handleNumberInput = (id: string, field: 'temperature' | 'ph' | 'dissolvedOxygen' | 'manualDissolvedOxygen' | 'manualDissolvedOxygenSaturation' | 'salinity', value: string) => {
    if (!canEditWaterLogs || !claimWaterRowForEdit(id)) return
    const numValue = value === '' ? null : parseFloat(value)
    if (value !== '' && (isNaN(numValue!) || numValue! < 0)) return
    
    // Auto-estimate DO and saturation when temperature changes, including clearing estimates when temperature is removed.
    if (field === 'temperature') {
      const newData = data.map(row => 
        row.id === id 
          ? { ...row, temperature: numValue }
          : row
      )
      const dataWithEstimates = recalculateEstimatedWaterDissolvedOxygen(newData, getCurrentFarmAltitude())
      const updated = dataWithEstimates.find((row) => row.id === id)
      if (!updated) return
      persistWaterRowUpsert(dataWithEstimates, updated.id)
    } else {
      updateCell(id, field, numValue)
    }
  }

  const selectedFarmLabel = useMemo(() => {
    if (!farmId) return '—'
    const selected = availableFarms.find((farm: any) => String(farm.id ?? '') === String(farmId))
    return selected?.name ?? String(farmId)
  }, [availableFarms, farmId])

  const selectedProductionUnitLabel = useMemo(() => {
    if (!productionUnitId) return '—'
    const selected = availableProductionUnits.find((unit: any) => String(unit.id ?? '') === String(productionUnitId))
    return selected?.name ?? String(productionUnitId)
  }, [availableProductionUnits, productionUnitId])

  // Rows whose lot was transferred to another production unit — their reading
  // stays labelled with that lot (read-only) instead of showing "(ninguno)".
  const foreignLotIds = useMemo(
    () => getForeignWaterLotIds(data, availableLots, farmLots),
    [data, availableLots, farmLots]
  )

  // Sort rows by clicked column header (mirrors the Database table sort UX).
  const waterCorrectionCounts = useCorrectionCounts(
    'water-log',
    data.map((row) => row.id),
    waterCorrectionsRefreshKey
  )

  const sortedData = useMemo(() => {
    if (!sortConfig) return data
    const copy = [...data]
    copy.sort((a, b) => compareForSort({
      aValue: (a as Record<string, unknown>)[sortConfig.key as string],
      bValue: (b as Record<string, unknown>)[sortConfig.key as string],
      direction: sortConfig.direction,
    }))
    return copy
  }, [data, sortConfig])

  // Findings #206: readings of a deleted lot are kept but hidden behind
  // "Mostrar", like Base de datos; shown, they are greyed and read-only.
  // Readings of a lot that moved away stay: they are the unit's water history.
  const [showDeletedLotRows, setShowDeletedLotRows] = useState(false)
  const deletedLotRowIds = useMemo(() => {
    const ids = new Set<string>()
    if (deletedLotLabels.size === 0) return ids
    const activeIds = new Set(availableLots.map((lot) => String(lot.id)))
    data.forEach((row) => {
      const lotId = String((row as { lotId?: unknown }).lotId ?? '').trim()
      if (lotId && !activeIds.has(lotId) && deletedLotLabels.has(lotId)) ids.add(row.id)
    })
    return ids
  }, [data, availableLots, deletedLotLabels])
  const visibleData = useMemo(
    () => (showDeletedLotRows ? sortedData : sortedData.filter((row) => !deletedLotRowIds.has(row.id))),
    [sortedData, showDeletedLotRows, deletedLotRowIds]
  )

  const renderSortableHeader = useCallback((
    key: keyof WaterDataRow,
    labelKey: TranslationKey,
    extraStyle?: React.CSSProperties,
  ) => {
    const isSorted = sortConfig?.key === key
    let dir = ''
    if (isSorted) dir = sortConfig?.direction === 'asc' ? ' ▲' : ' ▼'
    return (
      <th
        style={{
          padding: '15px 12px',
          border: '1px solid #ddd',
          textAlign: 'left',
          fontWeight: 'bold',
          cursor: 'pointer',
          userSelect: 'none',
          ...extraStyle,
        }}
        onClick={() => setSortConfig(prev => {
          if (prev?.key === key) {
            return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
          }
          return { key, direction: 'asc' }
        })}
      >
        {t(labelKey)}{dir}
      </th>
    )
  }, [sortConfig, t])

  return (
    <div className="responsive-container">
      <h2 style={{ marginTop: 0 }}>{t('water.title')}</h2>
      {!canEditWaterLogs && (
        <p style={{ marginTop: 4, fontSize: 12, opacity: 0.8 }}>
          {t('water.readonly.bannerPrefix')}
          {principal?.role ?? 'viewer'}
          {t('water.readonly.bannerMiddle')} {t('water.readonly.bannerSuffix')}
        </p>
      )}
      <CurrentContextBar
        fields={[
          { label: t('water.context.facility'), value: selectedFarmLabel },
          { label: t('water.context.productionUnit'), value: selectedProductionUnitLabel }
        ]}
      />
      <div className="responsive-toolbar">
        <ContextSelectorBar
          farm={{
            label: t('water.context.facility'),
            value: farmId,
            onChange: (value) => {
              void flushPendingWaterEdits()
              setFarmId(value)
              setAvailableLots([])
              setLotAutoClearedIds(new Set())
              setIsContextSwitching(true)
            },
            placeholder: t('water.placeholder.selectFacility'),
            options: availableFarms.map((farm: any) => ({
              key: farm.id ?? farm.name,
              value: String(farm.id ?? ''),
              label: farm.name
            }))
          }}
          productionUnit={{
            label: t('water.context.productionUnit'),
            value: productionUnitId,
            onChange: (value) => {
              void flushPendingWaterEdits()
              setProductionUnitId(value)
              setAvailableLots([])
              setLotAutoClearedIds(new Set())
              setIsContextSwitching(true)
            },
            disabled: !farmId || availableProductionUnits.length === 0,
            placeholder: !farmId ? t('water.placeholder.selectFacilityFirst') : t('water.placeholder.selectProductionUnit'),
            options: buildUnitSelectOptions(availableProductionUnits, t('unit.inactive.suffix'))
          }}
          summary={null}
        />

        <div className="responsive-actions" style={{ justifyContent: 'flex-end' }}>
          <GradientButton onClick={exportCSV} variant="primary" size="sm">{t('water.action.exportCsv')}</GradientButton>
        </div>
      </div>

      {(!farmId || !productionUnitId) && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: '#6c757d' }}>
          ⓘ {t('water.hint.selectContextForImport')}
        </div>
      )}

      {importReviewNotice ? (
        <div style={{
          marginTop: '10px',
          padding: '10px 14px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '16px' }}>ℹ️</span>
          <div style={{ color: '#166534', flex: 1 }}>{importReviewNotice}</div>
        </div>
      ) : null}

      {waterTableStatusMessage && (
        <div style={{
          marginTop: '10px',
          padding: '10px 14px',
          backgroundColor: '#eef6ff',
          border: '1px solid #c8ddff',
          borderRadius: '6px',
          color: '#19407c'
        }}>
          {waterTableStatusMessage}
        </div>
      )}

      {waterSyncLastResultSummary && (
        <div style={{
          marginTop: '10px',
          padding: '10px 14px',
          border: '1px solid #bfdbfe',
          borderRadius: '6px',
          backgroundColor: '#eff6ff',
          color: '#1d4ed8'
        }}>
          <div>{waterSyncLastResultSummary.resultLabel}</div>
          <div>{waterSyncLastResultSummary.attemptedAtLabel}</div>
        </div>
      )}

      {/* v2.1 finding #17 + #53: when no result, attention, or transient
          action toast is being surfaced, render a persistent baseline so
          the operator always has a stable anchor that says "sync is
          healthy" — same parity as Database. Visibility is computed by
          `computeWaterSyncBannerVisibility` so the supersede contract
          (baseline never coexists with another slot) is unit-testable. */}
      {farmId && productionUnitId && computeWaterSyncBannerVisibility({
        hasActionMessage: Boolean(waterTableStatusMessage),
        hasAttentionMessage: Boolean(waterSyncAttentionMessage),
        hasLastResultSummary: Boolean(waterSyncLastResultSummary)
      }).showBaseline && (
        <div
          data-testid="water-sync-baseline"
          style={{
            marginTop: '10px',
            padding: '10px 14px',
            border: '1px solid #d1fae5',
            borderRadius: '6px',
            backgroundColor: '#ecfdf5',
            color: '#047857',
            fontSize: 13
          }}
        >
          {t('water.sync.baselineCaughtUp')}
        </div>
      )}

      {waterSyncAttentionMessage && (
        <div style={{
          marginTop: '10px',
          padding: '10px 14px',
          border: '1px solid #fed7aa',
          borderRadius: '6px',
          backgroundColor: '#fff7ed',
          color: '#9a3412'
        }}>
          <div>{waterSyncAttentionMessage}</div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <GradientButton
              onClick={() => void handleWaterSyncRetry()}
              disabled={retryingWaterSync || waterTableRefreshing}
            >
              {retryingWaterSync ? t('water.sync.retrying') : t('water.sync.retryAction')}
            </GradientButton>
            <GradientButton
              onClick={() => void refreshWaterLogs()}
              disabled={waterTableRefreshing || retryingWaterSync}
            >
              {waterTableRefreshing ? t('water.sync.refreshing') : t('water.sync.refreshAction')}
            </GradientButton>
          </div>
        </div>
      )}

      {farmId && productionUnitId && (
        <>
          {showDateNotification && (
            <div style={{
              marginBottom: '15px',
              padding: '12px',
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '16px' }}>⚠️</span>
              <div>
                <strong style={{ color: '#856404' }}>{t('water.dateNotice.title')}</strong>
                <span style={{ color: '#856404', marginLeft: '5px' }}>
                  {t('water.dateNotice.message')}
                </span>
              </div>
              <button
                onClick={() => setShowDateNotification(false)}
                style={{
                  marginLeft: 'auto',
                  background: 'none',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: '#856404'
                }}
                title={t('water.notification.closeTitle')}
              >
                ✕
              </button>
            </div>
          )}

          {selectedUnitInactive ? (
            <div data-testid="water-unit-inactive-notice" role="status" style={{ margin: '10px 0', padding: '10px 12px', borderRadius: 10, border: '1px solid #fde68a', backgroundColor: '#fffbeb', color: '#92400e', fontSize: 13 }}>
            {t('unit.inactive.captureBlocked')}
          </div>
          ) : null}
          {productionUnitId ? (
            <WaterCapacityStatus
              lotIds={availableLots.filter((lot) => !lot.stage || lot.stage === 'Active').map((lot) => lot.id)}
              refreshKey={data}
            />
          ) : null}
          <div className="responsive-actions" style={{ marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
            <GradientButton
              onClick={addRow}
              disabled={!canEditWaterLogs || selectedUnitInactive}
            >
              {t('water.action.addMeasurement')}
            </GradientButton>
            <GradientButton
              onClick={() => void refreshWaterLogs()}
              disabled={!farmId || !productionUnitId || waterTableRefreshing}
            >
              {t('water.action.refreshLogs')}
            </GradientButton>
          </div>

          <div className={isContextSwitching ? 'table-shell table-shell-switching' : 'table-shell table-shell-ready'} style={{ maxWidth: '1200px', width: '100%', margin: '0 auto' }}>
            <div
              ref={tableScrollTopRef}
              onScroll={syncScroll('top')}
              style={{
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                border: '2px solid #ddd',
                borderBottom: 'none',
                borderRadius: '8px 8px 0 0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                height: 12,
                background: '#f8f9fa'
              }}
            >
              <div
                style={{
                  width: Math.max(tableWidth, tableScrollRef.current?.clientWidth || 0),
                  height: 1,
                }}
              />
            </div>
            <div
              ref={tableScrollRef}
              onScroll={syncScroll('main')}
              style={{
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                border: '2px solid #ddd',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <DeletedLotRowsLine
                count={deletedLotRowIds.size}
                shown={showDeletedLotRows}
                onToggle={() => setShowDeletedLotRows((value) => !value)}
                testId="water-hidden-deleted-lot-rows"
              />
              <table ref={tableRef} style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  {renderSortableHeader('date', 'water.table.date', {
                    minWidth: 220,
                    maxWidth: 280,
                    whiteSpace: 'normal',
                  })}
                  {renderSortableHeader('lot', 'water.table.lot')}
                  {renderSortableHeader('temperature', 'water.table.temperature', { backgroundColor: '#fff3cd' })}
                  {renderSortableHeader('ph', 'water.table.ph')}
                  {renderSortableHeader('dissolvedOxygen', 'water.table.doEstimated', { backgroundColor: '#f0f9ff' })}
                  {renderSortableHeader('dissolvedOxygenSaturation', 'water.table.doSaturationEstimated', { backgroundColor: '#f0f9ff' })}
                  {renderSortableHeader('manualDissolvedOxygen', 'water.table.doManual')}
                  {renderSortableHeader('manualDissolvedOxygenSaturation', 'water.table.doSaturationManual')}
                  {renderSortableHeader('salinity', 'water.table.salinity')}
                  <th style={{ padding: '15px 12px', border: '1px solid #ddd', textAlign: 'left', fontWeight: 'bold' }}>
                    {t('water.table.flow')}
                  </th>
                  {renderSortableHeader('notes', 'water.table.notes')}
                  <th style={{ padding: '15px 12px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 'bold' }}>
                    {t('water.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {visibleData.map(row => {
                  const tempStatus = getTemperatureStatus(row.temperature, farmSpeciesKey)
                  const effectiveManualSaturation = getDerivedManualDissolvedOxygenSaturation(row, currentFarmAltitude)
                  const showsDerivedManualSaturation = row.manualDissolvedOxygenSaturation === null && effectiveManualSaturation !== null
                  const isAutoClearedLot = lotAutoClearedIds.has(row.id)
                  const selectedLotId = String((row as any).lotId ?? '').trim()
                  const lotUiState = resolveUnmappedLotUiState({
                    unmappedLotLabel: (row as any).unmappedLot,
                    availableLots
                  })
                  const { unmappedLot, hasUnmappedLot, isAmbiguousLot, canCreateNewLot } = lotUiState
                  const savedLotLabel = String(row.lot ?? '').trim()
                  const isForeignLot = foreignLotIds.has(row.id)
                  const deletedLotLabel = selectedLotId && !availableLots.some((lot) => String(lot.id) === selectedLotId)
                    ? deletedLotLabels.get(selectedLotId)
                    : undefined
                  const isDeletedLot = deletedLotLabel !== undefined
                  const lotCellDisplayMode = isDeletedLot ? 'savedLabel' : resolveWaterLotCellDisplayMode({
                    savedLotLabel,
                    hasUnmappedLot,
                    availableLotsCount: availableLots.length,
                    isForeignLot
                  })
                  // findings #66: saved readings are changed via "Corregir…"
                  // (audited); rows added in this visit stay editable inline.
                  const isDeletedLotRow = deletedLotRowIds.has(row.id)
                  const rowLocked = isWaterRowLocked(row.id) || isDeletedLotRow
                  return (
                    <tr
                      key={row.id}
                      title={isDeletedLotRow ? t('datatable.deletedLot.readOnly') : (rowLocked && canEditWaterLogs ? t('water.lockedRowHint') : undefined)}
                      style={isDeletedLotRow ? DELETED_LOT_ROW_STYLE : undefined}
                      data-deleted-lot={isDeletedLotRow ? 'true' : undefined}
                    >
                      <td style={{
                        ...styles.dateCell,
                        backgroundColor: invalidDates.has(row.id) ? '#fee' : 'white'
                      }}>
                        <DateTimeLocalInput
                          value={String(row.date ?? '')}
                          timeZone={waterTimeZone}
                          disabled={!canEditWaterLogs || rowLocked}
                          onChangeValue={(nextValue) => {
                            updateCell(row.id, 'date', nextValue)
                          }}
                          onBlur={() => {
                            void flushPendingWaterRowUpsert(row.id)
                          }}
                          invalid={invalidDates.has(row.id)}
                          pickerOnly
                          onRequestReveal={scrollDateInputIntoView}
                          style={{
                            ...styles.input,
                            borderColor: invalidDates.has(row.id) ? '#dc3545' : '#ccc'
                          }}
                          placeholder={t('water.date.placeholder')}
                        />
                        {invalidDates.has(row.id) && (
                          <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px' }}>{t('water.date.invalidHint')}</div>
                        )}
                        <CrewRowBadges
                          captureSource={row.captureSource}
                          capturedByActorId={row.capturedByActorId}
                          correctionCount={waterCorrectionCounts.get(row.id) ?? 0}
                          actorNames={actorNames}
                          testId={`water-row-badges-${row.id}`}
                        />
                      </td>
                      <td style={{
                        ...styles.cell,
                        backgroundColor: isAutoClearedLot ? '#fee' : (styles.cell as any).background
                      }}>
                        {lotCellDisplayMode === 'savedLabel' ? (
                          <div style={{ fontSize: '14px', padding: '4px 0', color: isDeletedLot ? '#64748b' : undefined }} title={isDeletedLot ? t('water.lot.deletedTitle') : isForeignLot ? t('water.lot.transferredTitle') : undefined} data-testid={isDeletedLot ? `water-row-deleted-lot-${row.id}` : undefined}>
                            {savedLotLabel || deletedLotLabel}
                            {isDeletedLot && (
                              <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>
                                {' '}({t('water.lot.deletedTag')})
                              </span>
                            )}
                            {isForeignLot && (
                              <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>
                                {' '}({t('water.lot.transferredTag')})
                              </span>
                            )}
                          </div>
                        ) : lotCellDisplayMode === 'select' ? (
                          <>
                            <select
                              value={selectedLotId}
                              onChange={(e) => {
                                if (!canEditWaterLogs) return
                                const nextLotId = String(e.target.value)
                                const lot = availableLots.find((l) => String(l.id) === nextLotId)
                                const nextLotKey = lot?.lotKey ?? ''
                                const nextData = data.map((r) => {
                                  if (r.id !== row.id) return r
                                  return { ...r, lotId: nextLotId, lot: nextLotKey, unmappedLot: '' }
                                })

                                if (lotAutoClearedIds.has(row.id)) {
                                  setLotAutoClearedIds((prev) => {
                                    const cp = new Set(prev)
                                    cp.delete(row.id)
                                    return cp
                                  })
                                }

                                const updated = nextData.find((r) => r.id === row.id)
                                saveData(nextData, updated ? { syncRows: [updated] } : undefined)
                              }}
                              style={{ ...styles.input, padding: '4px' }}
                              disabled={lotsLoading || !canEditWaterLogs}
                            >
                              <option value="">{t('water.option.none')}</option>
                              {availableLots.map((lot) => (
                                <option key={lot.id} value={lot.id} disabled={!isLotActiveForInteraction(lot.stage)}>
                                  {formatLotPickerLabel(lot)}
                                </option>
                              ))}
                            </select>
                            {(isAutoClearedLot || hasUnmappedLot) && (
                              <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '4px' }}>
                                {hasUnmappedLot
                                  ? `${isAmbiguousLot ? t('water.lot.ambiguousPrefix') : t('water.lot.unmappedPrefix')} ${unmappedLot}`
                                  : t('water.lot.clearedNotInList')}
                              </div>
                            )}
                            {hasUnmappedLot && (
                              <div style={{ marginTop: '6px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {canCreateNewLot && (
                                  // Findings #199: lots are created only in Lotes, with their stocking date.
                                  <span style={{ fontSize: '11px', color: '#64748b', alignSelf: 'center' }}>{t('water.lot.createInLotsHint')}</span>
                                )}
                                <GradientButton
                                  variant="secondary"
                                  size="sm"
                                  disabled={!canEditWaterLogs}
                                  onClick={() => clearUnmappedLotForRow(row.id)}
                                >
                                  {t('water.lot.ignoreUnmapped')}
                                </GradientButton>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {/* M-Y1: empty-roster notice scoped to this create/edit slot only — never
                                hides the column, and never invents a disabled select with synthetic options. */}
                            <div style={{ fontSize: '11px', color: '#64748b' }}>
                              {lotsLoading
                                ? t('water.lot.loadingLots')
                                : selectedFarm && selectedProductionUnit
                                  ? t('water.lot.noneYet')
                                  : t('water.lot.selectContext')}
                            </div>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                              {selectedFarm && selectedProductionUnit
                                ? t('water.lot.createInLotsHelp')
                                : t('water.lot.selectContextHelp')}
                            </div>
                            {hasUnmappedLot && (
                              <div style={{ fontSize: '11px', color: '#dc3545', marginTop: '6px' }}>
                                {`${isAmbiguousLot ? t('water.lot.ambiguousPrefix') : t('water.lot.unmappedPrefix')} ${unmappedLot}`}
                              </div>
                            )}
                            {hasUnmappedLot && (
                              <div style={{ marginTop: '6px' }}>
                                <GradientButton
                                  variant="secondary"
                                  size="sm"
                                  disabled={!canEditWaterLogs}
                                  onClick={() => clearUnmappedLotForRow(row.id)}
                                >
                                  {t('water.lot.ignoreUnmapped')}
                                </GradientButton>
                              </div>
                            )}
                            <div style={{ marginTop: '6px' }}>
                              <GradientButton
                                variant="primary"
                                size="sm"
                                onClick={() => navigate('/lots')}
                                disabled={!selectedFarm || !selectedProductionUnit}
                              >
                                {t('water.lot.goToLots')}
                              </GradientButton>
                            </div>
                          </>
                        )}
                      </td>
                      <td style={{ ...styles.cell, backgroundColor: '#fff3cd' }}>
                        <input
                          type="number"
                          step="0.1"
                          value={row.temperature ?? ''}
                          onChange={(e) => handleNumberInput(row.id, 'temperature', e.target.value)}
                          onBlur={() => {
                            void flushPendingWaterRowUpsert(row.id)
                          }}
                          disabled={!canEditWaterLogs || rowLocked}
                          style={styles.input}
                          placeholder={t('water.placeholder.celsius')}
                        />
                        {row.temperature !== null && (
                          <div style={{ 
                            fontSize: '11px', 
                            color: tempStatus.color, 
                            fontWeight: 'bold',
                            marginTop: '2px'
                          }}>
                            {t(tempStatus.labelKey)}
                          </div>
                        )}
                      </td>
                      <td style={styles.cell}>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="14"
                          value={row.ph ?? ''}
                          onChange={(e) => handleNumberInput(row.id, 'ph', e.target.value)}
                          onBlur={() => {
                            void flushPendingWaterRowUpsert(row.id)
                          }}
                          disabled={!canEditWaterLogs || rowLocked}
                          style={styles.input}
                          placeholder={t('water.placeholder.ph')}
                        />
                      </td>
                      <td style={{ ...styles.cell, backgroundColor: '#f0f9ff' }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: '#0c4a6e',
                          padding: '4px'
                        }}>
                          {row.dissolvedOxygen === null ? '-' : `${row.dissolvedOxygen} mg/L`}
                        </div>
                        {row.temperature !== null && (
                          <div style={{
                            fontSize: '11px',
                            color: '#0369a1',
                            fontStyle: 'italic'
                          }}>
                            {t('water.do.autoFromPrefix')} {row.temperature}°C
                          </div>
                        )}
                      </td>
                      <td style={{ ...styles.cell, backgroundColor: '#f0f9ff' }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: '#0c4a6e',
                          padding: '4px'
                        }}>
                          {row.dissolvedOxygenSaturation === null ? '-' : `${row.dissolvedOxygenSaturation}%`}
                        </div>
                        {row.temperature !== null && row.dissolvedOxygen !== null && (
                          <div style={{
                            fontSize: '11px',
                            color: '#0369a1',
                            fontStyle: 'italic'
                          }}>
                            {t('water.do.vsSeaLevelStandard')}
                          </div>
                        )}
                      </td>
                      <td style={styles.cell}>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={row.manualDissolvedOxygen ?? ''}
                          onChange={(e) => handleNumberInput(row.id, 'manualDissolvedOxygen', e.target.value)}
                          onBlur={() => {
                            void flushPendingWaterRowUpsert(row.id)
                          }}
                          disabled={!canEditWaterLogs || rowLocked}
                          style={styles.input}
                          placeholder={t('water.placeholder.manualReading')}
                        />
                      </td>
                      <td style={styles.cell}>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="200"
                          value={row.manualDissolvedOxygenSaturation ?? ''}
                          onChange={(e) => handleNumberInput(row.id, 'manualDissolvedOxygenSaturation', e.target.value)}
                          onBlur={() => {
                            void flushPendingWaterRowUpsert(row.id)
                          }}
                          disabled={!canEditWaterLogs || rowLocked}
                          style={styles.input}
                          placeholder={t('water.placeholder.manualSaturation')}
                        />
                        {showsDerivedManualSaturation && (
                          <div style={{
                            fontSize: '11px',
                            color: '#0369a1',
                            fontStyle: 'italic',
                            marginTop: '2px'
                          }}>
                            {t('water.do.manualSaturationDerived', { value: effectiveManualSaturation.toFixed(1) })}
                          </div>
                        )}
                      </td>
                      <td style={styles.cell}>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={row.salinity ?? ''}
                          onChange={(e) => handleNumberInput(row.id, 'salinity', e.target.value)}
                          onBlur={() => {
                            void flushPendingWaterRowUpsert(row.id)
                          }}
                          disabled={!canEditWaterLogs || rowLocked}
                          style={styles.input}
                          placeholder={t('water.placeholder.salinity')}
                        />
                      </td>
                      <td style={styles.cell}>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          value={readFlowLpm(row) ?? ''}
                          onChange={(e) => handleFlowInput(row.id, e.target.value)}
                          onBlur={() => {
                            void flushPendingWaterRowUpsert(row.id)
                          }}
                          disabled={!canEditWaterLogs || rowLocked}
                          style={styles.input}
                          placeholder="L/min"
                          data-testid="water-flow-input"
                        />
                      </td>
                      <td style={styles.cell}>
                        <input
                          type="text"
                          value={row.notes}
                          onChange={(e) => updateCell(row.id, 'notes', e.target.value)}
                          onBlur={() => {
                            void flushPendingWaterRowUpsert(row.id)
                          }}
                          disabled={!canEditWaterLogs || rowLocked}
                          style={styles.input}
                          placeholder={t('water.placeholder.observations')}
                        />
                      </td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                          <CrewCorrectionButton
                            resource="water-log"
                            entityId={row.id}
                            farmId={farmId || undefined}
                            disabled={!canEditWaterLogs || isDeletedLotRow}
                            onDelete={(reason) => deleteRow(row.id, reason)}
                            onDone={async () => {
                              await reloadWaterFromApi()
                              setWaterCorrectionsRefreshKey((key) => key + 1)
                            }}
                          />
                          {!rowLocked ? (
                            <GradientButton
                              onClick={() => { void deleteRow(row.id, t('water.delete.draftReason')) }}
                              variant="danger"
                              size="sm"
                              disabled={!canEditWaterLogs}
                            >
                              {t('common.delete')}
                            </GradientButton>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {data.length === 0 && (
                  <tr>
                    <td 
                      colSpan={9} 
                      style={{ 
                        padding: '40px', 
                        textAlign: 'center', 
                        color: '#666',
                        fontStyle: 'italic'
                      }}
                    >
                      {t('water.empty.title')}
                      <br />
                      {t('water.empty.subtitlePrefix')}
                      {t('water.empty.subtitleAction')}
                      {t('water.empty.subtitleSuffix')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '8px',
            padding: '16px',
            marginTop: '16px'
          }}>
            <h4 style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#0c4a6e',
              margin: '0 0 8px 0'
            }}>
              {t('water.info.do.heading')}
            </h4>
            <div style={{
              fontSize: '13px',
              color: '#0c4a6e',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '12px'
            }}>
              <div>
                <strong>{t('water.info.do.baseFormula')}</strong> DO = 14.652 - 0.41022×T + 0.007991×T² - 0.000077774×T³
              </div>
              <div>
                <strong>{t('water.info.do.altitudeCorrection')}</strong> P/P₀ = (1 - 0.0065×h/288.15)^5.257
              </div>
              <div>
                <strong>{t('water.info.do.currentAltitudePrefix')}</strong> {currentFarmAltitude}m a.s.l. ({t('water.info.do.pressureRatioPrefix')} {currentPressureRatio.toFixed(3)})
              </div>
              <div>
                <strong>{t('water.info.do.examplesPrefix')} {currentFarmAltitude}m:</strong>
                {currentFarmAltitude === 0 
                  ? t('water.info.do.examplesSeaLevel')
                  : `10°C → ${estimateDissolvedOxygen(10, currentFarmAltitude)} mg/L | 20°C → ${estimateDissolvedOxygen(20, currentFarmAltitude)} mg/L`
                }
              </div>
            </div>
            <p style={{
              margin: '8px 0 0 0',
              fontSize: '12px',
              color: '#0369a1'
            }}>
              {t('water.info.do.tip')}
            </p>
          </div>

          {/* Findings #143: the generic water-quality ranges were removed — they depend on species and could mislead. */}
        </>
      )}
    </div>
  )
}